import type { QueriesObject, QueryResults, System } from '../../odie';
import type { BlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { BasicBlockComponent } from '../components/BasicBlockComponent';
import { RainbowBubbleBlockComponent } from '../components/RainbowBubbleBlockComponent';
import { PositionType } from '../../lib/defs/types';

export class RainbowBubbleSystem implements System {
  public static readonly NAME = 'rainbowBubble';
  public static Queries: QueriesObject = {
    block: {
      components: [BasicBlockComponent],
      removed: true,
    },
    rainbowBubbles: {
      components: [RainbowBubbleBlockComponent],
    },
  };
  public scene!: GameScene;
  public queries!: QueryResults;

  public removedFromQuery(entity: BlockEntity) {
    // Skip if applying extra powerups
    if (this.scene.isApplyingExtraPowerups) return;
    // Skip if block is not basic
    if (entity.c.block.props.type !== 'basic') return;
    // Skip if no rainbow bubbles
    if (this.queries.rainbowBubbles.entities.length === 0) return;
    // Clear surrounding rainbow bubbles
    this.clearSurroundingRainbowBubbles(entity);
  }

  private clearSurroundingRainbowBubbles(entity: BlockEntity) {
    // List surrounding positions that could be affected
    const surroudingPositions: PositionType[] = [
      { x: -1, y: 0 },
      { x: 1, y: 0 },
      { x: 0, y: -1 },
      { x: 0, y: 1 },
    ];
    // Clear rainbow bubbles over empty cells in the surrounding positions
    for (const position of surroudingPositions) {
      this.clearRainbowBubbleOverEmtpyCells(entity, position);
    }
  }

  private clearRainbowBubbleOverEmtpyCells(
    entity: BlockEntity,
    positionDiff: PositionType,
  ) {
    const mapComponent = this.scene.sessionEntity.c.map;
    const position = entity.c.position.mapPosition;
    const cell = mapComponent.getCellAt({
      x: position.x + positionDiff.x,
      y: position.y + positionDiff.y,
    });
    // Skip if cell is not found
    if (!cell) return;
    // Only proceed on empty cells (no base entity)
    if (cell.base?.entity) return;
    // Find overlay entity in the cell
    const overlayEntity = cell.overlay?.entity;
    // Skip if overlay is not rainbow bubble
    if (overlayEntity?.c.block.blockId !== 'rainbowBubble') return;
    // Skip if no view2d component is found, just in case
    if (!overlayEntity?.c.view2d) return;
    // Skip if overlay is still animating spawn
    if (overlayEntity.c.view2d.view.alpha < 1) return;
    // Perform attack on the rainbow bubble
    overlayEntity.c.block.collide('attack');
  }
}
